import { View, Text, ScrollView } from "react-native";
import H4 from "../Typography/H4";
import FontWeightType from "../../models/FontWeightType";
import useGenericContentStore from "../../store/genericContent.store";
import useToggleStore from "../../store/useToggle.store";
import { globalAny } from "../../utils/Utils";
import ControllerContent from "../ControllerContent";
import {
	StyledBackgroundView,
	StyledBottomBorder,
	StyledContainer,
	StyledFont,
	StyledTextContent,
	StyledTouchableOpacity,
	StyledTouchableOpacityNarrow,
	TabsContainer,
	TabsNarrowContainer,
	TabsNarrowContentArea,
	TabsNarrowMain,
} from "./styled";
import { Props } from "./model";

const TabsView = ({ tabsToRender, children, controller, channelData, onController, isSettings, isTDP, paddingLeft }: Props) => {
	const selectedTabs = useGenericContentStore((state) => state.selectedTabs);
	const setSelectedTabs = useGenericContentStore((state) => state.setSelectedTabs);
	const isNarrow = useGenericContentStore((state) => state.isNarrow);
	const isToggleMQTT = useToggleStore((state: any) => state.isToggleMQTT);

	/**
	 * Handles tab press by updating the selected tab index.
	 *
	 * @param {number} index - The index of the tab that was pressed.
	 */
	const handleTabPress = (index: number) => setSelectedTabs(index);

	/**
	 * Renders the narrow view of the tab component for mobile or compact layouts.
	 * Displays a horizontal tab bar with touchable items. Highlights the selected tab,
	 * and disables tabs based on their `disabled` flag.
	 *
	 * If `channelData` exists, the selected tab is the third one (index 2),
	 * and `isToggleMQTT` is true, a `ControllerContent` component is shown.
	 *
	 * Below the tabs, the corresponding tab content is rendered based on the selected tab.
	 * For settings view, the content is wrapped in a `ScrollView`.
	 *
	 * @returns {JSX.Element} - The complete narrow tab layout.
	 */
	const renderNarrowTabs = () => (
		<TabsNarrowMain isSettings={isSettings}>
			<TabsNarrowContainer isSettings={isSettings}>
				{tabsToRender.map((tab: any, index: number) => (
					//@ts-ignores
					<StyledTouchableOpacityNarrow
						key={`tabs-name-narrow-${tab.title}`}
						shouldHighlight={index === selectedTabs}
						onPress={() => handleTabPress(index)}
						disabled={tab.disabled}
					>
						<StyledFont disabled={tab.disabled}>{tab.title}</StyledFont>
					</StyledTouchableOpacityNarrow>
				))}
				{channelData && selectedTabs === 2 && isToggleMQTT && (
					<ControllerContent
						data={controller}
						isTitle={channelData?.length <= 1}
						ribbonTitle={globalAny.language.assign_single_screen_message}
						onPress={onController}
						isNarrow={isNarrow}
					/>
				)}
			</TabsNarrowContainer>

			<TabsNarrowContentArea isSettings={isSettings}>
				{isTDP && children[selectedTabs]}
				{isSettings && <ScrollView>{children[selectedTabs]}</ScrollView>}
			</TabsNarrowContentArea>
		</TabsNarrowMain>
	);

	/**
	 * Renders the wide version of the tab layout, typically for larger screens or desktop views.
	 * Displays horizontal tabs using styled touchable components.
	 *
	 * Each tab highlights when selected, and can be disabled.
	 * If the tab is selected, a background and bottom border indicator are shown.
	 *
	 * Below the tab headers, the corresponding child content is rendered based on the selected tab.
	 *
	 * @returns {JSX.Element} - The complete wide tab layout.
	 */
	const renderWideTabs = () => (
		//@ts-ignores
		<TabsContainer isSettings={isSettings}>
			{/* @ts-ignores */}
			<StyledContainer paddingLeft={paddingLeft} isSettings={isSettings}>
				{tabsToRender.map((tab: any, index: number) => (
					//@ts-ignores
					<StyledTouchableOpacity
						key={`tabs-name-${tab.title}`}
						shouldHighlight={index === selectedTabs}
						onPress={() => handleTabPress(index)}
						disabled={tab.disabled}
					>
						{/* @ts-ignores */}
						<StyledTextContent>
							<H4 fontWeight={FontWeightType.Medium}>
								<Text style={{ opacity: tab.disabled ? 0.5 : 1 }}>{tab.title}</Text>
							</H4>
						</StyledTextContent>
						{index === selectedTabs && (
							<>
								<StyledBackgroundView />
								<StyledBottomBorder />
							</>
						)}
					</StyledTouchableOpacity>
				))}
			</StyledContainer>

			<View style={{ width: "100%", paddingLeft: isSettings ? 30 : 0 }}>{children[selectedTabs]}</View>
		</TabsContainer>
	);

	const shouldRenderNarrow = isNarrow && (isTDP || isSettings);
	return shouldRenderNarrow ? renderNarrowTabs() : renderWideTabs();
};

export default TabsView;
